import { notAuthenticated } from "@/sdk/transport/errors"

export type Settled<T> = { ok: true; value: T } | { ok: false; error: unknown }

export type FlushMeta = { waitedMs: number; staleRejected: number }

type Waiter<TInput, TResult> = {
  input: TInput
  identity: string
  resolve: (value: TResult) => void
  reject: (error: unknown) => void
}

type Options<TInput, TResult> = {
  cap: number
  // Unset (the default) batches by tick: what one microtask queue holds goes
  // together, at no added latency. A window is only for callers that arrive
  // staggered by their own awaits, and it taxes every lone call with the wait.
  windowMs?: number
  // A flush also splits when its inputs' sizes would exceed maxSize (a URL budget, say).
  sizeOf?: (input: TInput) => number
  maxSize?: number
  identity: () => string
  flush: (items: TInput[], meta: FlushMeta) => Promise<Settled<TResult>[]>
}

/**
 * Coalesces the calls of one tick into one flush. Drains chain on a promise
 * tail, so at most one flush is in flight and they land in arrival order; an
 * item is sent as the identity that enqueued it or not at all.
 */
export class Batcher<TInput, TResult> {
  private pending: Waiter<TInput, TResult>[] = []
  private tail: Promise<void> = Promise.resolve()
  private scheduled = false
  private timer: ReturnType<typeof setTimeout> | null = null
  private firstPushedAt = 0

  constructor(private readonly opts: Options<TInput, TResult>) {}

  push(input: TInput): Promise<TResult> {
    return new Promise((resolve, reject) => {
      if (!this.pending.length) this.firstPushedAt = Date.now()
      this.pending.push({
        input,
        identity: this.opts.identity(),
        resolve,
        reject
      })
      this.schedule(this.pending.length >= this.opts.cap)
    })
  }

  // Rejects what is still queued; a flush already on the wire settles from its response.
  destroy(): void {
    if (this.timer) clearTimeout(this.timer)
    this.timer = null

    const destroyed = new Error("Batcher was destroyed")
    this.pending.splice(0).forEach(waiter => waiter.reject(destroyed))
  }

  private schedule(immediate: boolean): void {
    if (immediate && this.timer) {
      clearTimeout(this.timer)
      this.timer = null
    }
    if (this.scheduled || this.timer) return

    if (!immediate && this.opts.windowMs) {
      this.timer = setTimeout(() => {
        this.timer = null
        this.enqueueDrain()
      }, this.opts.windowMs)
    } else {
      this.scheduled = true
      queueMicrotask(() => {
        this.scheduled = false
        this.enqueueDrain()
      })
    }
  }

  // The tail is the serialization point: a drain owns the snapshot it took,
  // so anything pushed while it runs waits for the next link.
  private enqueueDrain(): void {
    const owned = this.pending.splice(0)
    if (!owned.length) return

    const waitedMs = Date.now() - this.firstPushedAt
    this.tail = this.tail.then(() => this.drain(owned, waitedMs))
  }

  private async drain(
    owned: Waiter<TInput, TResult>[],
    waitedMs: number
  ): Promise<void> {
    let staleRejected = 0

    // Each chunk sits behind the previous one's request, so the identity is
    // read again before every send.
    for (const chunk of this.chunk(owned)) {
      const identity = this.opts.identity()
      const batch = chunk.filter(waiter => waiter.identity === identity)
      chunk
        .filter(waiter => waiter.identity !== identity)
        .forEach(waiter => waiter.reject(notAuthenticated()))
      staleRejected += chunk.length - batch.length
      if (!batch.length) continue

      try {
        const settled = await this.opts.flush(
          batch.map(waiter => waiter.input),
          { waitedMs, staleRejected }
        )
        batch.forEach((waiter, index) => {
          const outcome = settled[index]
          if (outcome?.ok) waiter.resolve(outcome.value)
          else waiter.reject(outcome?.error ?? new Error("No result"))
        })
      } catch (error) {
        batch.forEach(waiter => waiter.reject(error))
      }
    }
  }

  // Count cap and size budget, whichever bites first; an oversized single
  // input still travels alone rather than never.
  private chunk(
    waiters: Waiter<TInput, TResult>[]
  ): Waiter<TInput, TResult>[][] {
    const { cap, sizeOf, maxSize } = this.opts
    const chunks: Waiter<TInput, TResult>[][] = []
    let current: Waiter<TInput, TResult>[] = []
    let size = 0

    waiters.forEach(waiter => {
      const weight = sizeOf ? sizeOf(waiter.input) : 0
      const overBudget = maxSize !== undefined && size + weight > maxSize
      if (current.length && (current.length >= cap || overBudget)) {
        chunks.push(current)
        current = []
        size = 0
      }
      current.push(waiter)
      size += weight
    })
    if (current.length) chunks.push(current)

    return chunks
  }
}
